import { apiClient } from '../services/apiClient';
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

// ─── Types ────────────────────────────────────────────

export interface AffectedPlaylistDto {
  playlistId: string;
  playlistName: string;
  playlistType: string;
}

export interface AffectedPlaylistsResponseDto {
  affectedPlaylists: AffectedPlaylistDto[];
  totalCount: number;
}

export interface StreamUrlResponse {
  streamUrl: string;
  sasToken: string | null;
}

export interface LibraryTrackDto {
  id: string;
  title: string;
  artist: string;
  album: string | null;
  genre: string | null;
  mood: string | null;
  tempo: string | null;
  durationSeconds: number;
  /** Audio-processing pipeline status (formerly `status`). */
  processingStatus: string;
  /** Lifecycle status — replaces the old `isHidden` flag. */
  status: 'active' | 'inactive';
  fileUrl: string | null;
  processingError: string | null;
  streamRelativePath: string | null;
  thumbnailUrl: string | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  // ── Licensing / rights metadata (016-track-licensing) ──
  licenseId: string | null;
  licenseName: string | null;
  rightsHolder: string | null;
  rightsTerritory: string | null;
  contract: string | null;
  rightsHolderTrackId: string | null;
  /** ISO date "YYYY-MM-DD". */
  rightsValidFrom: string | null;
  /** ISO date "YYYY-MM-DD". */
  rightsValidTo: string | null;
}

export interface LicenseDto {
  id: string;
  name: string;
  isSystem: boolean;
  inUse: boolean;
  createdAt: string;
}

/** Editable licensing/rights fields sent on PATCH /admin/library/{trackId}. */
export interface UpdateLibraryTrackPayload {
  title: string | null;
  artist: string | null;
  album: string | null;
  genre: string | null;
  mood: string | null;
  tempo: string | null;
  tags: string[];
  licenseId: string | null;
  rightsHolder: string | null;
  rightsTerritory: string | null;
  contract: string | null;
  rightsHolderTrackId: string | null;
  rightsValidFrom: string | null;
  rightsValidTo: string | null;
}

export interface LibraryTrackPageDto {
  items: LibraryTrackDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface FetchLibraryTracksParams {
  page: number;
  pageSize?: number;
  search?: string;
  genre?: string;
  mood?: string;
  status?: string;
  /**
   * Comma-separated list of tags. AND / intersection semantics applied server-side
   * (FR-060): a track must carry every supplied tag to remain in the result set.
   */
  tags?: string;
  sortBy?: string;
  sortDesc?: boolean;
  sortOrder?: string;
}

export interface CuratedPlaylistTrackDto {
  id: string;
  contentId: string;
  title: string | null;
  artist: string | null;
  durationSeconds: number | null;
  position: number;
}

export interface CuratedPlaylistDetailDto {
  id: string;
  name: string;
  description: string | null;
  targetUseCase: string | null;
  thumbnailUrl: string | null;
  isPublished: boolean;
  trackCount: number;
  totalDurationSeconds: number;
  usageCount: number;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdByAdminName: string;
  tracks: CuratedPlaylistTrackDto[];
}

// ─── API Methods ──────────────────────────────────────

export function fetchLibraryTracks(params: FetchLibraryTracksParams): Promise<LibraryTrackPageDto> {
  return apiClient.get<LibraryTrackPageDto>('/admin/library', {
    page: params.page,
    pageSize: params.pageSize ?? 20,
    ...(params.search && { search: params.search }),
    ...(params.genre && { genre: params.genre }),
    ...(params.mood && { mood: params.mood }),
    ...(params.status && { status: params.status }),
    ...(params.tags && { tags: params.tags }),
    ...(params.sortBy && { sortBy: params.sortBy }),
    ...(params.sortDesc !== undefined && { sortDesc: params.sortDesc }),
  });
}

export function reprocessTrack(trackId: string): Promise<LibraryTrackDto> {
  return apiClient.post<LibraryTrackDto>(`/admin/library/${trackId}/reprocess`);
}

export function activateTrack(trackId: string): Promise<LibraryTrackDto> {
  return apiClient.post<LibraryTrackDto>(`/admin/library/${trackId}/activate`);
}

export function deactivateTrack(trackId: string): Promise<LibraryTrackDto> {
  return apiClient.post<LibraryTrackDto>(`/admin/library/${trackId}/deactivate`);
}

export function updateLibraryTrack(
  trackId: string,
  payload: UpdateLibraryTrackPayload,
): Promise<LibraryTrackDto> {
  return apiClient.patch<LibraryTrackDto>(`/admin/library/${trackId}`, payload);
}

// ─── Licenses (016-track-licensing) ───────────────────

export function fetchLicenses(): Promise<LicenseDto[]> {
  return apiClient.get<LicenseDto[]>('/admin/licenses');
}

export function createLicense(name: string): Promise<LicenseDto> {
  return apiClient.post<LicenseDto>('/admin/licenses', { name });
}

export function deleteLicense(licenseId: string): Promise<void> {
  return apiClient.delete<void>(`/admin/licenses/${licenseId}`);
}

export function getAffectedPlaylists(trackId: string): Promise<AffectedPlaylistsResponseDto> {
  return apiClient.get<AffectedPlaylistsResponseDto>(`/admin/library/${trackId}/affected-playlists`);
}

export function getStreamUrl(trackId: string): Promise<StreamUrlResponse> {
  return apiClient.get<StreamUrlResponse>(`/admin/library/${trackId}/stream-url`);
}

export function fetchLibraryTags(): Promise<{ tags: string[] }> {
  return apiClient.get<{ tags: string[] }>('/admin/library/tags');
}

export interface LibraryFilterOptionsDto {
  genres: string[];
  moods: string[];
  tempos: string[];
  tags: string[];
}

export function fetchFilterOptions(): Promise<LibraryFilterOptionsDto> {
  return apiClient.get<LibraryFilterOptionsDto>('/admin/library/filter-options');
}

export function batchAddTracksToPlaylist(playlistId: string, contentIds: string[]): Promise<CuratedPlaylistDetailDto> {
  return apiClient.post<CuratedPlaylistDetailDto>(`/admin/curated-playlists/${playlistId}/tracks/batch`, { contentIds });
}

/** Removes the given tracks (by content id) from a curated playlist. Returns the removed count. */
export function batchRemoveTracksFromPlaylist(
  playlistId: string, contentIds: string[],
): Promise<{ removedCount: number }> {
  return apiClient.post<{ removedCount: number }>(
    `/admin/curated-playlists/${playlistId}/tracks/batch-remove`, { contentIds });
}

/** A curated playlist + which of the queried tracks it contains (powers the manage-playlists tool). */
export interface PlaylistMembershipDto {
  playlistId: string;
  playlistName: string;
  memberTrackIds: string[];
}

/** For a set of tracks, returns each curated playlist that contains at least one of them. */
export function getPlaylistMembership(trackIds: string[]): Promise<PlaylistMembershipDto[]> {
  return apiClient.post<PlaylistMembershipDto[]>('/admin/library/playlist-membership', { trackIds });
}

/** Lightweight curated-playlist list item — what the upload "add to playlist" picker needs. */
export interface CuratedPlaylistListItemDto {
  id: string;
  name: string;
  description: string | null;
  thumbnailUrl: string | null;
  trackCount: number;
  isPublished: boolean;
}

interface CuratedPlaylistListResponseDto {
  items: CuratedPlaylistListItemDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/**
 * Fetches a flat list of curated playlists for the upload "add to playlist" picker.
 * Pulls a single large page (the picker is a simple list, not a paged table); the
 * backend GET /admin/curated-playlists endpoint is 1-indexed.
 */
export async function fetchCuratedPlaylists(): Promise<CuratedPlaylistListItemDto[]> {
  const response = await apiClient.get<CuratedPlaylistListResponseDto>('/admin/curated-playlists', {
    page: 1,
    pageSize: 100,
  });
  return response.items;
}

export function reorderPlaylistTracks(playlistId: string, trackIds: string[]): Promise<void> {
  return apiClient.put<void>(`/admin/curated-playlists/${playlistId}/tracks/reorder`, { trackIds });
}

// ─── Bulk metadata edit (FR-076–FR-078) ───────────────

/**
 * Field names accepted in the bulk-update `fields` mask. Only the fields named
 * here are written on each track; everything else is left untouched (so a partial
 * bulk edit never wipes the rest of a track's metadata). Matches the backend
 * `BulkUpdateTrackFields.Allowed` set.
 */
export type BulkUpdateTrackField =
  | 'artist'
  | 'genre'
  | 'mood'
  | 'tempo'
  | 'album'
  | 'tags'
  | 'licenseId'
  | 'rightsHolder'
  | 'rightsTerritory'
  | 'contract'
  | 'rightsHolderTrackId'
  | 'rightsValidFrom'
  | 'rightsValidTo'
  | 'status';

/**
 * Request body for POST /admin/library/bulk-update. `fields` is the opt-in mask
 * of field names to apply across every track in `trackIds`. Tags use REPLACE
 * semantics (the supplied list becomes each track's full tag set). A `null`
 * `licenseId` clears the license. `status` is the wire form "active"|"inactive".
 */
export interface BulkUpdateTracksBody {
  trackIds: string[];
  fields: BulkUpdateTrackField[];
  artist?: string;
  genre?: string;
  mood?: string;
  tempo?: string;
  album?: string;
  tags?: string[];
  licenseId?: string | null;
  rightsHolder?: string;
  rightsTerritory?: string;
  contract?: string;
  rightsHolderTrackId?: string;
  /** ISO date "YYYY-MM-DD". */
  rightsValidFrom?: string;
  /** ISO date "YYYY-MM-DD". */
  rightsValidTo?: string;
  status?: 'active' | 'inactive';
}

export interface BulkUpdateTracksResponse {
  updatedCount: number;
}

/**
 * Applies a single metadata change to many tracks at once via the backend bulk
 * endpoint (audited per track, server-side). Only the fields listed in `fields`
 * are written; the matching value keys are read from `values`. The change applies
 * to every track everywhere it is used (curated + customer playlists).
 */
export function bulkUpdateTracks(
  trackIds: string[],
  fields: BulkUpdateTrackField[],
  values: Omit<BulkUpdateTracksBody, 'trackIds' | 'fields'>,
): Promise<BulkUpdateTracksResponse> {
  return apiClient.post<BulkUpdateTracksResponse>('/admin/library/bulk-update', {
    trackIds,
    fields,
    ...values,
  });
}

export interface BulkDeleteTracksResponse {
  deletedCount: number;
}

/**
 * Hard-deletes many library tracks at once via the backend bulk endpoint
 * (audited per track). Ids that no longer exist are skipped server-side.
 */
export function bulkDeleteTracks(trackIds: string[]): Promise<BulkDeleteTracksResponse> {
  return apiClient.post<BulkDeleteTracksResponse>('/admin/library/bulk-delete', { trackIds });
}

// ─── Hard Delete Types ────────────────────────────────

export interface DeleteImpactPlaylistDto {
  id: string;
  name: string;
  trackCountAfterRemoval: number;
}

export interface DeleteImpactCustomerPlaylistDto {
  id: string;
  name: string;
  organizationName: string;
  trackCountAfterRemoval: number;
}

export interface HardDeleteImpactBlobFiles {
  originalBlobPath: string | null;
  processedBlobPath: string | null;
}

export interface HardDeleteImpactResponseDto {
  trackId: string;
  trackTitle: string;
  curatedPlaylists: DeleteImpactPlaylistDto[];
  customerPlaylists: DeleteImpactCustomerPlaylistDto[];
  blobFiles: HardDeleteImpactBlobFiles;
  totalAffectedPlaylists: number;
}

// ─── Hard Delete API Methods ──────────────────────────

export function getDeleteImpact(trackId: string): Promise<HardDeleteImpactResponseDto> {
  return apiClient.get<HardDeleteImpactResponseDto>(`/admin/library/${trackId}/delete-impact`);
}

export function hardDeleteTrack(trackId: string): Promise<void> {
  return apiClient.delete<void>(`/admin/library/${trackId}`);
}

// ─── Hard Delete React Query Hooks ────────────────────

export function useDeleteImpact(
  trackId: string,
  options?: Omit<UseQueryOptions<HardDeleteImpactResponseDto>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: ['library', 'delete-impact', trackId],
    queryFn: () => getDeleteImpact(trackId),
    enabled: !!trackId,
    ...options,
  });
}

export function useHardDeleteTrack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (trackId: string) => hardDeleteTrack(trackId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['library'] });
      queryClient.invalidateQueries({ queryKey: ['admin-library-infinite'] });
    },
  });
}
